import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { Shield, Tag, Star, ArrowRight, CheckCircle, Zap, Check } from 'lucide-react';
import BaseUrl from '../../ApiServices/BaseUrl';
import loadRazorpay from "../../ApiServices/loadRazorpay";
import Swal from 'sweetalert2';
import axios from 'axios';
import './BadgePromotionSection.css';
import { getVisibleSourceLabel, hasHighlightedSourceLabel, formatApplyForCountry } from '../../../utils/candidateBadges';

const BadgePromotionSection = ({ profile, onPurchaseSuccess }) => {
    const [selectedOption, setSelectedOption] = useState('offer_tag');
    const [isProcessing, setIsProcessing] = useState(false);
    const [prices, setPrices] = useState({
        badge_price: 10,
        tag_price: 10,
        both_price: 18
    });

    useEffect(() => {
        const fetchPrices = async () => {
            try {
                const [badge, tag, both] = await Promise.all([
                    axios.get(`${BaseUrl}/api/admin/settings/badge_price`),
                    axios.get(`${BaseUrl}/api/admin/settings/tag_price`),
                    axios.get(`${BaseUrl}/api/admin/settings/both_price`)
                ]);
                
                setPrices({
                    badge_price: badge.data?.setting_value || 10,
                    tag_price: tag.data?.setting_value || 10,
                    both_price: both.data?.setting_value || 18
                });
            } catch (err) {
                console.error('Error fetching promotion prices:', err);
            }
        };

        fetchPrices();
    }, []);

    const isVerified = String(profile?.verification_status || '').toLowerCase() === 'verified';
    const hasVerifiedTag = Number(profile?.purchased_badge) === 1;
    const hasOfferBadge = Number(profile?.purchased_tag) === 1;
    const hasBothAddOns = hasVerifiedTag && hasOfferBadge;

    // Options mapping
    const options = [
        {
            id: 'verified_badge',
            title: 'Verified Tag',
            description: 'Show a verified tick and tag above your profile photo.',
            price: prices.badge_price,
            icon: <CheckCircle size={20} className="text-success" />,
            color: '#28a745'
        },
        {
            id: 'offer_tag',
            title: 'Offer Badge',
            description: 'Get a premium "Offer" badge on your profile photo and a "Very Active" tag on your card.',
            price: prices.tag_price,
            icon: <Tag size={20} className="text-primary" />,
            color: '#007bff',
            featured: true,
            featuredLabel: 'Recommended'
        },
        {
            id: 'both',
            title: 'Verified + Offer Badge',
            description: 'Get both, save money, and get a premium "Very Active" status label.',
            price: prices.both_price,
            icon: <Star size={20} className="text-warning" />,
            color: '#ffc107',
            featured: true,
            featuredLabel: 'Best Value'
        }
    ];

    const getOptionState = (optionId) => {
        if (hasBothAddOns) {
            return { disabled: true, purchased: true };
        }

        if (optionId === 'verified_badge' && hasVerifiedTag) {
            return { disabled: true, purchased: true };
        }

        if (optionId === 'offer_tag' && hasOfferBadge) {
            return { disabled: true, purchased: true };
        }

        if (optionId === 'both' && (hasVerifiedTag || hasOfferBadge)) {
            return { disabled: true, purchased: false };
        }

        return { disabled: false, purchased: false };
    };

    const selectableOptions = options.filter((opt) => !getOptionState(opt.id).disabled);

    useEffect(() => {
        if (selectableOptions.length === 0) {
            return;
        }

        if (!selectableOptions.some(opt => opt.id === selectedOption)) {
            setSelectedOption(selectableOptions[0].id);
        }
    }, [selectedOption, selectableOptions]);

    const currentOption = options.find(opt => opt.id === selectedOption) || selectableOptions[0] || options[0];
    const currentOptionState = currentOption ? getOptionState(currentOption.id) : { disabled: true, purchased: false };
    const hasSelectableOptions = selectableOptions.length > 0;

    const handlePayment = async () => {
        if (!currentOption) return;

        if (currentOptionState.disabled) {
            return;
        }

        if (!isVerified) {
            Swal.fire({
                icon: 'warning',
                title: 'You are not verified yet',
                text: 'Pay the publishing fee to get verified first. After verification, you can buy add-ons.'
            });
            return;
        }

        setIsProcessing(true);
        try {
            const amount = currentOption.price;
            
            // 1. Create order
            const orderRes = await axios.post(`${BaseUrl}/api/orders`, {
                amount: Math.round(amount * 100), // in paise, ensure integer
                currency: 'USD'
            });

            const { order_id } = orderRes.data;

            // 2. Open Razorpay
            const rzpOptions = {
                key: process.env.REACT_APP_RAZORPAY_KEY,
                amount: amount * 100,
                currency: 'USD',
                name: "Gulf Worker",
                description: `Purchase ${currentOption.title}`,
                order_id: order_id,
                handler: async (response) => {
                    try {
                        // 3. Verify and update backend
                        const verifyRes = await axios.post(`${BaseUrl}/api/candidate/purchase-addon`, {
                            candidate_id: profile.user_id,
                            addon_type: selectedOption,
                            amount_paid: amount,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id
                        });

                        if (verifyRes.data.success) {
                            Swal.fire('Success!', 'Your badge/tag has been purchased successfully.', 'success');
                            if (onPurchaseSuccess) onPurchaseSuccess();
                        }
                    } catch (err) {
                        Swal.fire('Error', 'Payment verification failed.', 'error');
                    }
                },
                prefill: {
                    name: profile.first_name,
                    email: profile.email_id,
                    contact: profile.whatsapp_number
                },
                theme: { color: "#3d6e6e" }
            };

            const isRazorpayReady = await loadRazorpay();
            if (!isRazorpayReady) {
                Swal.fire('Error', 'Payment gateway failed to load.', 'error');
                return;
            }

            const rzp = new window.Razorpay(rzpOptions);
            rzp.open();

        } catch (err) {
            console.error('Payment Error:', err);
            Swal.fire('Error', 'Could not initialize payment.', 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    if (!profile) {
        return null;
    }

    return (
        <div className="badge-promotion-section mb-4">
            <Card className="border-0 shadow-sm overflow-hidden">
                <Card.Body className="p-0">
                    <Row className="g-0">
                        {/* Left: Options Selection */}
                        <Col md={7} className="p-4 p-lg-5">
                            <div className="mb-4">
                                <h3 className="section-title fw-bold mb-3 d-flex align-items-center gap-2">
                        <Zap className="text-warning" size={28} />
                        Boost Your Profile Visibility
                    </h3>
                                <p className="text-muted">Stand out to employers by adding verified badges and offer tags to your profile card.</p>
                            </div>

                            <div className="promotion-options">
                                {options.map(opt => {
                                    const optionState = getOptionState(opt.id);
                                    const isActive = selectedOption === opt.id && !optionState.disabled;

                                    return (
                                    <div 
                                        key={opt.id}
                                        className={`promotion-option-card ${isActive ? 'active' : ''} ${optionState.disabled ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (!optionState.disabled) {
                                                setSelectedOption(opt.id);
                                            }
                                        }}
                                    >
                                        <div className="option-icon-wrap">
                                            {opt.icon}
                                        </div>
                                        <div className="option-info">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <h5 className="option-title mb-1">{opt.title}</h5>
                                                <span className="option-price">${opt.price}</span>
                                            </div>
                                            <p className="option-desc mb-0">{opt.description}</p>
                                            {optionState.disabled && (
                                                <div className="option-status-text">
                                                    {optionState.purchased ? 'Already purchased' : 'Not available after your current add-on'}
                                                </div>
                                            )}
                                        </div>
                                        <div className="option-check">
                                            {isActive && <Check size={18} />}
                                            {optionState.disabled && <Check size={18} />}
                                        </div>
                                        {opt.featured && <span className="featured-badge">{opt.featuredLabel || 'Best Value'}</span>}
                                    </div>
                                    );
                                })}
                            </div>

                            <Button 
                                className="w-100 mt-4 purchase-btn d-flex align-items-center justify-content-center gap-2"
                                size="lg"
                                onClick={handlePayment}
                                disabled={isProcessing || !hasSelectableOptions || currentOptionState.disabled}
                            >
                                {isProcessing
                                    ? 'Processing...'
                                    : hasSelectableOptions
                                        ? `Get ${currentOption.title} Now`
                                        : 'All Add-ons Already Purchased'}
                                {!isProcessing && hasSelectableOptions && <ArrowRight size={20} />}
                            </Button>
                            
                            <div className="validity-container">
                                <span className="validity-tag">
                                    <Shield size={16} className="validity-icon" /> 1 Year Validity
                                </span>
                            </div>
                        </Col>

                        {/* Right: Live Preview */}
                        <Col md={5} className="preview-column p-4 d-flex flex-column align-items-center justify-content-start bg-light">
                            <h5 className="fw-bold mb-4">Live Preview</h5>
                            
                            <div className="preview-card-container" style={{ marginTop: '35px' }}>
                                <div className="preview-card-mockup">
                                    {/* Mock Profile Card based on CandidateCard logic */}
                                    <div className="mock-card shadow-sm border rounded p-2 bg-white" style={{ maxWidth: '380px' }}>
                                        <div className="d-flex">
                                            {/* Left: photo & badge */}
                                            <div className="me-3 text-center" style={{ width: '100px' }}>
                                                <div className="d-flex justify-content-center gap-2 mb-2" style={{ minHeight: '18px' }}>
                                                    {(selectedOption === 'verified_badge' || selectedOption === 'both' || profile.purchased_badge) ? (
                                                        <div className="custom-label verified-label" style={{ fontSize: '0.6rem', padding: '0', height: '16px' }}>
                                                            <span className="verified-label-icon" style={{ fontSize: '0.7rem' }}>&#10003;</span>
                                                            <span>Verified</span>
                                                        </div>
                                                    ) : null}
                                                </div>

                                                <div className="position-relative d-flex justify-content-center profile-img-preview" style={{ width: '100px', height: '100px' }}>
                                                    <div className="rounded-circle overflow-hidden border position-relative" style={{ width: '100px', height: '100px' }}>
                                                        <img 
                                                            src={profile.profile_photo || 'https://via.placeholder.com/100'} 
                                                            alt="Preview" 
                                                            className="w-100 h-100" 
                                                            style={{ objectFit: 'cover' }}
                                                        />
                                                        {(selectedOption === 'offer_tag' || selectedOption === 'both' || profile.purchased_tag) && (
                                                            <div className="offer-badge-overlay" style={{ bottom: '15px', fontSize: '0.6rem', padding: '1px 0' }}>
                                                                assigned
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="very-active-label" style={{ 
                                                    fontSize: '0.7rem',
                                                    marginTop: '5px',
                                                    color: (selectedOption === 'offer_tag' || selectedOption === 'both' || hasHighlightedSourceLabel(profile)) ? 'green' : 'inherit', 
                                                    fontWeight: (selectedOption === 'offer_tag' || selectedOption === 'both' || hasHighlightedSourceLabel(profile)) ? 'bold' : 'normal' 
                                                }}>
                                                    {(selectedOption === 'offer_tag' || selectedOption === 'both') ? 'Very Active' : getVisibleSourceLabel(profile)}
                                                </div>
                                            </div>

                                            {/* Right: details */}
                                            <div className="flex-grow-1" style={{ textAlign: 'left' }}>
                                                <h6 className="fw-bold mb-1" style={{ fontSize: '14px' }}>
                                                    {profile.first_name || 'Demo'} - {profile.age || '21'} years
                                                </h6>
                                                
                                                <ul className="ps-3 mb-0" style={{ fontSize: '0.7rem', listStyleType: 'disc', color: '#333' }}>
                                                    <li className="mb-1"><strong>Job Position -</strong> {Array.isArray(profile.job_position) ? profile.job_position[0] : (profile.job_position || 'Cleaner')}</li>
                                                    <li className="mb-1"><strong>Salary -</strong> {(() => {
                                                        const rawLocalSalary = String(profile.expected_monthly_salary || "").trim();
                                                        const rawUsdSalary = String(profile.expected_monthly_salary_usd || "").trim();
                                                        const isNegotiable =
                                                            profile.salary_negotiable === true ||
                                                            rawLocalSalary.toLowerCase() === "negotiable" ||
                                                            rawUsdSalary.toLowerCase() === "negotiable";

                                                        if (isNegotiable) return "Negotiable";

                                                        const usdAmount = parseFloat(rawUsdSalary);
                                                        if (!Number.isNaN(usdAmount) && usdAmount > 0) {
                                                            const dhsAmount = Math.round(usdAmount * 3.67);
                                                            return `${Math.round(usdAmount)} USD / ${dhsAmount} DHS`;
                                                        }

                                                        const localAmount = parseFloat(rawLocalSalary);
                                                        if (!Number.isNaN(localAmount) && localAmount > 0) {
                                                            return `${Math.round(localAmount)} ${profile.expected_monthly_salary_currency || ''}`;
                                                        }

                                                        return "Negotiable";
                                                    })()}</li>
                                                    <li className="mb-1"><strong>Experience -</strong> {profile.total_work_experience || '1 – 2 Years'}</li>
                                                    <li className="mb-1"><strong>Language -</strong> {(() => {
                                                        try {
                                                            const languages = typeof profile.language === 'string' ? JSON.parse(profile.language) : profile.language;
                                                            if (Array.isArray(languages) && languages.length > 0) {
                                                                 const firstLang = languages[0];
                                                                 return typeof firstLang === 'object' && firstLang.language ? firstLang.language : firstLang;
                                                            }
                                                        } catch (e) {}
                                                        return 'English';
                                                    })()}</li>
                                                    <li className="mb-1"><strong>Apply for -</strong> {formatApplyForCountry(profile.country_apply_for_job)}</li>
                                                    <li className="mb-1"><strong>Country -</strong> {profile.nationality || 'Afghanistan'}</li>
                                                    <li className="mb-1"><strong>Current Location -</strong> {profile.current_country || 'Afghanistan'}</li>
                                                    <li className="mb-1"><strong>Accommodation -</strong> {profile.accommodation_preference || 'Live in/Live out'}</li>
                                                    <li className="mb-1"><strong>Visa Status -</strong> {profile.visa_type || 'Not specified'}</li>
                                                    <li className="mb-1"><strong>Available From -</strong> {profile.preferred_start_date || 'Not specified'}</li>
                                                </ul>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="row g-1 mt-2">
                                            <div className="col-3">
                                                <div className="btn btn-light btn-sm w-100 d-flex align-items-center justify-content-center" style={{ height: '32px', border: '1px solid #eee' }}>
                                                    <i className="bi bi-camera-video"></i>
                                                </div>
                                            </div>
                                            <div className="col-3">
                                                <div className="btn btn-secondary btn-sm w-100 d-flex align-items-center justify-content-center" style={{ height: '32px', backgroundColor: '#7177a6', border: 'none', fontSize: '0.7rem' }}>
                                                    <i className="bi bi-telephone-fill me-1"></i> Call
                                                </div>
                                            </div>
                                            <div className="col-3">
                                                <div className="btn btn-primary btn-sm w-100 d-flex align-items-center justify-content-center" style={{ height: '32px', backgroundColor: '#007bff', border: 'none', fontSize: '0.7rem' }}>
                                                    <i className="bi bi-eye-fill me-1"></i> View
                                                </div>
                                            </div>
                                            <div className="col-3">
                                                <div className="btn btn-success btn-sm w-100 d-flex align-items-center justify-content-center" style={{ height: '32px', backgroundColor: '#198754', border: 'none' }}>
                                                    <i className="bi bi-whatsapp"></i>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Footer Links */}
                                        <div className="row g-0 mt-2 pt-2 border-top text-muted" style={{ fontSize: '0.65rem' }}>
                                            <div className="col-3 text-center"><i className="bi bi-heart me-1"></i> Like</div>
                                            <div className="col-3 text-center"><i className="bi bi-share me-1"></i> Share</div>
                                            <div className="col-3 text-center"><i className="bi bi-chat-dots me-1"></i> Message</div>
                                            <div className="col-3 text-center"><i className="bi bi-flag me-1"></i> Report</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="preview-reflection"></div>
                            </div>
                            
                            <div className="mt-4 text-center">
                                <p className="mb-0 text-muted small">This is how your card will look</p>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </div>
    );
};

export default BadgePromotionSection;
